import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class CameraPreferenceService {
  private readonly STORAGE_KEY = 'liveforms-preferred-camera-device';

  /**
   * Save the preferred camera device ID to localStorage
   */
  savePreferredCamera(deviceId: string): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, deviceId);
    } catch (error) {
      console.warn('Failed to save camera preference to localStorage:', error);
    }
  }

  /**
   * Get the preferred camera device ID from localStorage
   */
  getPreferredCamera(): string | null {
    try {
      return localStorage.getItem(this.STORAGE_KEY);
    } catch (error) {
      console.warn('Failed to get camera preference from localStorage:', error);
      return null;
    }
  }

  /**
   * Clear the preferred camera device ID from localStorage
   */
  clearPreferredCamera(): void {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
    } catch (error) {
      console.warn('Failed to clear camera preference from localStorage:', error);
    }
  }

  /**
   * Find the preferred camera from a list of available devices
   * Returns the preferred camera if found, otherwise returns the first back/rear camera,
   * or the first available camera as a fallback
   */
  selectPreferredCamera(devices: MediaDeviceInfo[]): MediaDeviceInfo | undefined {
    if (!devices || devices.length === 0) {
      return undefined;
    }

    const preferredDeviceId = this.getPreferredCamera();

    // Try to find the preferred camera
    if (preferredDeviceId) {
      const preferredDevice = devices.find((device) => device.deviceId === preferredDeviceId);
      if (preferredDevice) {
        return preferredDevice;
      } else {
        console.log('⚠️ Preferred camera not found in available devices');
      }
    }

    // Fallback: Find the first back/rear camera
    const backCamera = devices.find((device) => /back|rear|environment/i.test(device.label));
    if (backCamera) {
      return backCamera;
    }

    // Final fallback: Return the first available camera
    return devices[0];
  }
}
